// Tiny WebAudio synth — no external sound files. All tones are generated
// live from oscillators + exponential gain envelopes. Keeps the bundle small
// and lets the palette be tweaked from code. Respects:
//   - user mute toggle (localStorage 'tessera.audioMuted.v1')
//   - 'prefers-reduced-motion' as a secondary hint (we do NOT auto-mute on
//      this — some players want sound even with reduced motion — but we use
//      shorter envelopes)
//
// Usage: sfx.select(), sfx.slide(), sfx.merge(chainDepth), sfx.win(), sfx.lose().

const MUTE_KEY = 'tessera.audioMuted.v1';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = readMuted();

function readMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

function writeMuted(m: boolean) {
  try { localStorage.setItem(MUTE_KEY, m ? '1' : '0'); } catch { /* ignore */ }
}

function ensureCtx(): AudioContext | null {
  if (muted) return null;
  if (ctx) return ctx;
  try {
    const Ctor: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.18;
    master.connect(ctx.destination);
    // Wire resume listeners once. Three categories:
    //   1. Page visibility events — visibilitychange/pageshow/focus.
    //      Fire when the page comes back from background. Reliable on
    //      desktop browsers and most mobile WebViews EXCEPT iOS Capacitor,
    //      where multitasking sometimes suppresses them.
    //   2. Document-level touchstart/mousedown — fire on the user's first
    //      interaction after returning to the app. iOS treats these as
    //      "real user gestures," which is REQUIRED for resume() to succeed
    //      after a backgrounding-induced suspend. Belt and suspenders for
    //      the cases where category 1 doesn't fire.
    //   3. Inline check inside tone() — if the context is still suspended
    //      when a sound is requested, await the resume before scheduling
    //      the oscillator. Without this, fire-and-forget resume() would
    //      schedule the oscillator at a frozen currentTime, and iOS WebKit
    //      silently drops oscillators with start times in the past.
    wireResumeListeners();
    return ctx;
  } catch {
    return null;
  }
}

// Resume the AudioContext on every signal that the page just became visible
// OR the user just touched/clicked anything. Idempotent — safe to call
// resume() on a running context (no-op).
//
// The capture:true on the touch/click listeners is deliberate: they need to
// fire BEFORE any inner React handler so the resume is in flight by the time
// the slide/tap triggers tone(). Passive:true keeps them off the gesture
// scrolling fast-path so the listeners don't impact swipe responsiveness.
let resumeListenersWired = false;
function wireResumeListeners(): void {
  if (resumeListenersWired) return;
  resumeListenersWired = true;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const tryResume = () => {
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* swallow — next tone call will retry */ });
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) tryResume();
  });
  window.addEventListener('pageshow', tryResume);
  window.addEventListener('focus', tryResume);
  // The critical addition (v0.13.1): document-level pointer events.
  // Any touchstart/mousedown after return-from-background runs inside a
  // real user gesture, which is what iOS WebKit requires to resume an
  // AudioContext that was suspended by app backgrounding. Without this,
  // visibilitychange-based resume() can fail silently because the event
  // handler is not considered a user gesture context on some iOS versions.
  document.addEventListener('touchstart', tryResume, { passive: true, capture: true });
  document.addEventListener('mousedown', tryResume, { passive: true, capture: true });
}

function tone(
  freq: number,
  durationMs: number,
  opts: { type?: OscillatorType; attackMs?: number; peak?: number; delayMs?: number; freqEnd?: number } = {}
) {
  const c = ensureCtx();
  if (!c || !master) return;
  // The schedule path needs to wait for an in-flight resume() before
  // calling osc.start, otherwise iOS drops the oscillator silently. If
  // the context is already running, scheduleNow runs synchronously and
  // there's no perceptible latency. If suspended, we await the resume
  // and THEN schedule against the freshly-advanced currentTime. On iOS
  // this typically adds 50-150ms latency to the very first sound after
  // return-from-background — acceptable, and only happens once per
  // backgrounding cycle.
  const scheduleNow = () => {
    if (!ctx || !master) return;
    const type = opts.type ?? 'sine';
    const attackMs = opts.attackMs ?? 6;
    const peak = opts.peak ?? 0.6;
    const delayMs = opts.delayMs ?? 0;
    const start = ctx.currentTime + delayMs / 1000;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, start);
    if (opts.freqEnd !== undefined && opts.freqEnd !== freq) {
      osc.frequency.linearRampToValueAtTime(opts.freqEnd, start + durationMs / 1000);
    }

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peak, start + attackMs / 1000);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + durationMs / 1000);

    osc.connect(gain).connect(master);
    osc.start(start);
    osc.stop(start + durationMs / 1000 + 0.05);
  };

  if (c.state === 'running') {
    scheduleNow();
  } else {
    // Suspended → resume first, then schedule. The resume() call itself
    // succeeds inside a user-gesture context (which we're in if tone() was
    // triggered by a tap → React handler chain). We use .then() rather
    // than awaiting so tone() stays synchronous from the caller's POV.
    c.resume().then(scheduleNow).catch(() => { /* swallow */ });
  }
}

export const sfx = {
  isMuted(): boolean {
    return muted;
  },
  toggleMuted(): boolean {
    muted = !muted;
    writeMuted(muted);
    if (muted && ctx) ctx.suspend().catch(() => {});
    return muted;
  },
  select() {
    tone(660, 90, { type: 'sine', peak: 0.25 });
  },
  slide() {
    // Portamento 420→360Hz on the triangle voice — subtle freq drop over
    // 140ms gives the slide a sense of motion, reads as the tile actually
    // travelling. The sine overlay stays static for a steady harmonic.
    tone(420, 140, { type: 'triangle', peak: 0.3, freqEnd: 360 });
    tone(560, 120, { type: 'sine', peak: 0.18, delayMs: 30 });
  },
  merge(chainDepth: number) {
    // Chain 1 → a clean two-note arpeggio. Chain ≥2 → annihilation, brighter
    // and longer with a third higher harmonic.
    if (chainDepth >= 2) {
      tone(523, 120, { type: 'sine', peak: 0.45 }); // C5
      tone(659, 180, { type: 'sine', peak: 0.5, delayMs: 70 }); // E5
      tone(784, 260, { type: 'sine', peak: 0.55, delayMs: 160 }); // G5
      tone(1047, 320, { type: 'sine', peak: 0.3, delayMs: 260 }); // C6 sparkle
    } else {
      tone(523, 100, { type: 'sine', peak: 0.35 });
      tone(659, 140, { type: 'sine', peak: 0.35, delayMs: 60 });
    }
  },
  win() {
    // Win voicing differs from merge(2+) on purpose — this is the level-
    // done moment, not the chain-explodes moment. C-major 7 arpeggio with
    // a sustained top C: C5 → E5 → G5 → B5 (the 7th gives closure) → C6.
    // Longer envelopes so the notes ring into each other like a chord
    // resolving. Previously identical to merge(2+) and felt redundant.
    tone(523, 160, { type: 'sine', peak: 0.4 });                  // C5
    tone(659, 180, { type: 'sine', peak: 0.42, delayMs: 110 });   // E5
    tone(784, 260, { type: 'sine', peak: 0.45, delayMs: 260 });   // G5
    tone(988, 420, { type: 'sine', peak: 0.35, delayMs: 400 });   // B5 (7th)
    tone(1047, 620, { type: 'sine', peak: 0.38, delayMs: 520, attackMs: 24 }); // C6 sustained
  },
  lose() {
    tone(330, 180, { type: 'triangle', peak: 0.35 });
    tone(247, 280, { type: 'triangle', peak: 0.3, delayMs: 140 });
  },
};
